import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RequestPayload, SchedulePayload, VacationPolicy, VacationScope, ajustarFaltas, calendarioFerias, cancelarFerias, decidirFerias,
  definirEnderecoLocalidade, LocationAddress, eventosFerias, feriasDaEquipe, listarLocalidades, listarPoliticas, pendentesFerias, preverFerias,
  reenviarFerias, salvarPolitica, solicitarFerias, visaoFerias,
} from '../api/ferias';

export const FERIAS_KEYS = {
  all: ['pessoas', 'ferias'] as const,
  visao: (scope: VacationScope) => ['pessoas', 'ferias', 'visao', scope] as const,
  eventos: (scope: VacationScope, filters: object) => ['pessoas', 'ferias', 'eventos', scope, filters] as const,
  pendentes: (as: string) => ['pessoas', 'ferias', 'pendentes', as] as const,
  equipe: ['pessoas', 'ferias', 'equipe'] as const,
  calendario: (from: string, to: string, orgUnitId?: string) => ['pessoas', 'ferias', 'calendario', from, to, orgUnitId] as const,
  politicas: ['pessoas', 'ferias', 'politicas'] as const,
  localidades: ['pessoas', 'ferias', 'localidades'] as const,
};

function useWrite<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: FERIAS_KEYS.all }) });
}

export const useVisaoFerias = (scope: VacationScope, enabled = true) =>
  useQuery({ queryKey: FERIAS_KEYS.visao(scope), queryFn: () => visaoFerias(scope), enabled });
export const useEventosFerias = (scope: VacationScope, filters: { type?: string; origin?: string; from?: string; to?: string }, enabled = true) =>
  useQuery({ queryKey: FERIAS_KEYS.eventos(scope, filters), queryFn: () => eventosFerias(scope, filters), enabled });

export const useAjustarFaltas = () =>
  useWrite(({ periodId, absences, justification }: { periodId: string; absences: number; justification: string }) =>
    ajustarFaltas(periodId, absences, justification));
export const usePreverFerias = (bondId: string | null) => useMutation({ mutationFn: (p: RequestPayload) => preverFerias(bondId, p) });
export const useSolicitarFerias = (bondId: string | null) => useWrite((p: RequestPayload) => solicitarFerias(bondId, p));
export const useReenviarFerias = (asHr: boolean) =>
  useWrite(({ id, payload }: { id: string; payload: SchedulePayload }) => reenviarFerias(id, payload, asHr));
export const useCancelarFerias = (asHr: boolean) =>
  useWrite(({ id, justification }: { id: string; justification?: string }) => cancelarFerias(id, justification, asHr));

export const usePendentesFerias = (as: 'rh' | 'gestor', enabled = true) =>
  useQuery({ queryKey: FERIAS_KEYS.pendentes(as), queryFn: () => pendentesFerias(as), enabled });
export const useDecidirFerias = (as: 'rh' | 'gestor') =>
  useWrite(({ id, decision, comment }: { id: string; decision: string; comment?: string }) => decidirFerias(as, id, decision, comment));

export const useFeriasDaEquipe = (enabled = true) => useQuery({ queryKey: FERIAS_KEYS.equipe, queryFn: feriasDaEquipe, enabled });
export const useCalendarioFerias = (from: string, to: string, orgUnitId?: string) =>
  useQuery({ queryKey: FERIAS_KEYS.calendario(from, to, orgUnitId), queryFn: () => calendarioFerias(from, to, orgUnitId), enabled: !!from && !!to });

export const usePoliticasFerias = () => useQuery({ queryKey: FERIAS_KEYS.politicas, queryFn: listarPoliticas });
export const useSalvarPolitica = () =>
  useWrite(({ id, payload }: { id: string | null; payload: Partial<VacationPolicy> }) => salvarPolitica(id, payload));

export const useLocalidades = () => useQuery({ queryKey: FERIAS_KEYS.localidades, queryFn: listarLocalidades });
export const useDefinirEnderecoLocalidade = () =>
  useWrite(({ id, address }: { id: string; address: LocationAddress }) => definirEnderecoLocalidade(id, address));
